import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Input,
  Tag,
  Button,
  Space,
  Typography,
  Tooltip,
  Select,
  Spin,
} from "antd";
import { getAllVisitorsPaginated } from "@api/user/superAdmin";
import { useHistory, useLocation } from "react-router-dom";
import VisitorAnalytics from "./VisitorAnalytics";
import {
  BarChartOutlined,
  UnorderedListOutlined,
  GlobalOutlined,
  ReloadOutlined,
  SearchOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";

const { Search } = Input;
const { Text } = Typography;
const { Option } = Select;

// Country flags
const countryFlags = {
  'US': '🇺🇸', 'GB': '🇬🇧', 'UK': '🇬🇧', 'CA': '🇨🇦', 'AU': '🇦🇺', 'DE': '🇩🇪', 'FR': '🇫🇷',
  'IN': '🇮🇳', 'JP': '🇯🇵', 'CN': '🇨🇳', 'BR': '🇧🇷', 'MX': '🇲🇽', 'ES': '🇪🇸', 'IT': '🇮🇹',
  'NL': '🇳🇱', 'SE': '🇸🇪', 'SG': '🇸🇬', 'AE': '🇦🇪', 'RU': '🇷🇺', 'KR': '🇰🇷', 'PK': '🇵🇰',
  'BD': '🇧🇩', 'PH': '🇵🇭', 'ID': '🇮🇩', 'TH': '🇹🇭', 'VN': '🇻🇳', 'ZA': '🇿🇦', 'NG': '🇳🇬',
  'TR': '🇹🇷', 'PL': '🇵🇱', 'UA': '🇺🇦', 'NZ': '🇳🇿', 'IE': '🇮🇪', 'CH': '🇨🇭', 'IL': '🇮🇱',
};
const getFlag = (code) => countryFlags[code?.toUpperCase()] || '🌍';

const VisitorList = () => {
  const history = useHistory();
  const location = useLocation();
  const [visitors, setVisitors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [currentView, setCurrentView] = useState("analytics");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
  });

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const page = parseInt(searchParams.get("page") || "1", 10);
    const pageSize = parseInt(searchParams.get("pageSize") || "50", 10);
    const search = searchParams.get("search") || "";
    const view = searchParams.get("view") || "analytics";

    setSearchText(search);
    setCurrentView(view);
    setPagination({ current: page, pageSize, total: 0 });
    if (view === "list") {
      fetchVisitors(page, pageSize, search);
    }
  }, [location.search]);

  const fetchVisitors = (page, pageSize, searchText = "") => {
    setLoading(true);
    getAllVisitorsPaginated(page, pageSize, searchText)
      .then((res) => {
        setVisitors(res.data.visitors);
        setPagination((p) => ({
          ...p,
          total: res.data.paginationData.totalVisitors,
          current: res.data.paginationData.page,
          pageSize: res.data.paginationData.perPage,
        }));
      })
      .catch((err) => {
        console.error("Fetch visitors error", err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleViewChange = (view) => {
    setCurrentView(view);
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("view", view);
    history.replace({ search: searchParams.toString() });
    if (view === "list") {
      fetchVisitors(pagination.current, pagination.pageSize, searchText);
    }
  };

  const handlePaginationChange = (page, pageSize) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("page", page);
    searchParams.set("pageSize", pageSize);
    searchParams.set("view", "list");
    if (searchText) searchParams.set("search", searchText);

    history.push({ search: searchParams.toString() });
    setPagination({ ...pagination, current: page, pageSize });
    fetchVisitors(page, pageSize, searchText);
  };

  const handleSearch = (searchValue) => {
    setSearchText(searchValue);
    setPagination({ ...pagination, current: 1 });
    fetchVisitors(1, pagination.pageSize, searchValue);

    const searchParams = new URLSearchParams();
    searchParams.set("page", "1");
    searchParams.set("pageSize", pagination.pageSize);
    searchParams.set("view", "list");
    if (searchValue) searchParams.set("search", searchValue);
    history.push({ search: searchParams.toString() });
  };

  const formatUrl = (url) => {
    if (!url) return "-";
    try {
      const urlObj = new URL(url);
      return urlObj.pathname + (urlObj.search || "");
    } catch {
      return url;
    }
  };

  const formatSource = (source) => {
    if (!source || source === "naked") return "Direct";
    try {
      const urlObj = new URL(source);
      return urlObj.hostname.replace("www.", "");
    } catch {
      return source.length > 25 ? source.substring(0, 25) + "..." : source;
    }
  };

  const getSourceColor = (source) => {
    const s = formatSource(source).toLowerCase();
    if (s.includes("google")) return "#4285f4";
    if (s.includes("facebook") || s.includes("fb")) return "#1877f2";
    if (s.includes("twitter") || s.includes("x.com")) return "#1da1f2";
    if (s.includes("linkedin")) return "#0077b5";
    if (s.includes("instagram")) return "#e4405f";
    if (s === "direct") return "#6b7280";
    return "#8b5cf6";
  };

  const formatDate = (date) => {
    if (!date) return "-";
    const d = new Date(date);
    const now = new Date();
    const diff = now - d;

    // Less than 1 hour
    if (diff < 3600000) {
      const mins = Math.floor(diff / 60000);
      return `${mins}m ago`;
    }
    // Less than 24 hours
    if (diff < 86400000) {
      const hours = Math.floor(diff / 3600000);
      return `${hours}h ago`;
    }
    // Less than 7 days
    if (diff < 604800000) {
      const days = Math.floor(diff / 86400000);
      return `${days}d ago`;
    }
    // Otherwise show date
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const totalPages = Math.ceil(pagination.total / pagination.pageSize);

  return (
    <div style={{ background: "#f8fafc", minHeight: "100vh" }}>
      {/* Hero Banner with Tabs */}
      <div
        style={{
          background: "linear-gradient(135deg, #7c3aed 0%, #8b5cf6 50%, #a78bfa 100%)",
          padding: "28px 24px 20px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "-50%",
            right: "-10%",
            width: "400px",
            height: "400px",
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />

        <div style={{ maxWidth: "1400px", margin: "0 auto", position: "relative", zIndex: 1 }}>
          <Row align="middle" justify="space-between">
            <Col xs={24} lg={12}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 12,
                    background: "rgba(255,255,255,0.2)",
                    backdropFilter: "blur(10px)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <GlobalOutlined style={{ fontSize: 22, color: "#fff" }} />
                </div>
                <div>
                  <h1 style={{ fontSize: 26, fontWeight: 700, color: "#fff", margin: 0 }}>
                    Visitor Analytics
                  </h1>
                  <p style={{ fontSize: 13, color: "rgba(255,255,255,0.85)", margin: 0 }}>
                    Track visitor behavior, traffic sources, and engagement
                  </p>
                </div>
              </div>
            </Col>

            <Col xs={24} lg={12}>
              <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 12 }}>
                <div
                  style={{
                    display: "flex",
                    background: "rgba(255,255,255,0.15)",
                    borderRadius: 10,
                    padding: 4,
                  }}
                >
                  <button
                    onClick={() => handleViewChange("analytics")}
                    style={{
                      padding: "10px 20px",
                      fontSize: 14,
                      fontWeight: 600,
                      color: currentView === "analytics" ? "#7c3aed" : "rgba(255,255,255,0.9)",
                      background: currentView === "analytics" ? "#fff" : "transparent",
                      border: "none",
                      borderRadius: 8,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      transition: "all 0.2s ease",
                    }}
                  >
                    <BarChartOutlined />
                    Dashboard
                  </button>
                  <button
                    onClick={() => handleViewChange("list")}
                    style={{
                      padding: "10px 20px",
                      fontSize: 14,
                      fontWeight: 600,
                      color: currentView === "list" ? "#7c3aed" : "rgba(255,255,255,0.9)",
                      background: currentView === "list" ? "#fff" : "transparent",
                      border: "none",
                      borderRadius: 8,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      transition: "all 0.2s ease",
                    }}
                  >
                    <UnorderedListOutlined />
                    All Visitors
                  </button>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Content */}
      {currentView === "analytics" ? (
        <VisitorAnalytics showHeader={false} />
      ) : (
        <div style={{ padding: "24px", maxWidth: "1448px", margin: "0 auto" }}>
          {/* Enterprise Table Card */}
          <div
            style={{
              background: "#fff",
              borderRadius: 16,
              border: "1px solid #e2e8f0",
              overflow: "hidden",
            }}
          >
            {/* Table Header */}
            <div
              style={{
                padding: "16px 20px",
                borderBottom: "1px solid #f1f5f9",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 12,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <Text strong style={{ fontSize: 16, color: "#1e293b" }}>
                  Visitors
                </Text>
                <Tag color="purple" style={{ fontWeight: 600, fontSize: 12 }}>
                  {pagination.total.toLocaleString()} total
                </Tag>
              </div>
              <Space size="middle">
                <Search
                  placeholder="Search country, city, IP..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onSearch={handleSearch}
                  style={{ width: 240 }}
                  allowClear
                  prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
                />
                <Tooltip title="Refresh">
                  <Button
                    icon={<ReloadOutlined spin={loading} />}
                    onClick={() => fetchVisitors(pagination.current, pagination.pageSize, searchText)}
                  />
                </Tooltip>
              </Space>
            </div>

            {/* Table */}
            {loading ? (
              <div style={{ padding: 60, textAlign: "center" }}>
                <Spin size="large" />
              </div>
            ) : (
              <>
                {/* Table Header Row */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "180px 160px 1fr 140px 130px 100px",
                    padding: "10px 20px",
                    background: "#f8fafc",
                    borderBottom: "1px solid #e2e8f0",
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    color: "#64748b",
                  }}
                >
                  <div>Location</div>
                  <div>Source</div>
                  <div>Page</div>
                  <div>IP Address</div>
                  <div>Region</div>
                  <div style={{ textAlign: "right" }}>Time</div>
                </div>

                {/* Table Body */}
                <div style={{ maxHeight: "calc(100vh - 380px)", overflowY: "auto" }}>
                  {visitors.map((visitor, index) => (
                    <div
                      key={visitor._id}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "180px 160px 1fr 140px 130px 100px",
                        padding: "12px 20px",
                        borderBottom: index < visitors.length - 1 ? "1px solid #f1f5f9" : "none",
                        alignItems: "center",
                        fontSize: 13,
                        transition: "background 0.15s",
                        cursor: "default",
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = "#f8fafc"}
                      onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                    >
                      {/* Location */}
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 18 }}>{getFlag(visitor.meta?.country_code)}</span>
                        <div>
                          <div style={{ fontWeight: 500, color: "#1e293b" }}>
                            {visitor.meta?.country_name || "Unknown"}
                          </div>
                          <div style={{ fontSize: 11, color: "#94a3b8" }}>
                            {visitor.meta?.city || "-"}
                          </div>
                        </div>
                      </div>

                      {/* Source */}
                      <div>
                        <div
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            padding: "4px 10px",
                            background: `${getSourceColor(visitor.utm_source)}15`,
                            borderRadius: 6,
                            fontSize: 12,
                            fontWeight: 500,
                            color: getSourceColor(visitor.utm_source),
                          }}
                        >
                          <div
                            style={{
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              background: getSourceColor(visitor.utm_source),
                            }}
                          />
                          {formatSource(visitor.utm_source)}
                        </div>
                      </div>

                      {/* Page */}
                      <div style={{ minWidth: 0, paddingRight: 16 }}>
                        <Tooltip title={visitor.meta?.url}>
                          <Text
                            ellipsis
                            style={{
                              color: "#475569",
                              fontSize: 12,
                              fontFamily: "monospace",
                              display: "block",
                            }}
                          >
                            {formatUrl(visitor.meta?.url)}
                          </Text>
                        </Tooltip>
                      </div>

                      {/* IP */}
                      <div>
                        <code
                          style={{
                            fontSize: 11,
                            padding: "3px 8px",
                            background: "#f1f5f9",
                            borderRadius: 4,
                            color: "#64748b",
                          }}
                        >
                          {visitor.meta?.IPv4 || "-"}
                        </code>
                      </div>

                      {/* Region */}
                      <div style={{ fontSize: 12, color: "#64748b" }}>
                        {visitor.meta?.state || "-"}
                        {visitor.meta?.postal && (
                          <span style={{ marginLeft: 4, color: "#94a3b8" }}>
                            ({visitor.meta.postal})
                          </span>
                        )}
                      </div>

                      {/* Time */}
                      <div style={{ textAlign: "right" }}>
                        <Tooltip
                          title={new Date(visitor.createdAt).toLocaleString()}
                        >
                          <span
                            style={{
                              fontSize: 12,
                              color: "#64748b",
                              fontWeight: 500,
                            }}
                          >
                            {formatDate(visitor.createdAt)}
                          </span>
                        </Tooltip>
                      </div>
                    </div>
                  ))}

                  {visitors.length === 0 && (
                    <div style={{ padding: 60, textAlign: "center", color: "#94a3b8" }}>
                      No visitors found
                    </div>
                  )}
                </div>

                {/* Pagination */}
                <div
                  style={{
                    padding: "12px 20px",
                    borderTop: "1px solid #e2e8f0",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    background: "#fafbfc",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <Text style={{ fontSize: 13, color: "#64748b" }}>
                      Showing {((pagination.current - 1) * pagination.pageSize) + 1}-
                      {Math.min(pagination.current * pagination.pageSize, pagination.total)} of {pagination.total.toLocaleString()}
                    </Text>
                    <Select
                      value={pagination.pageSize}
                      onChange={(size) => handlePaginationChange(1, size)}
                      style={{ width: 100 }}
                      size="small"
                    >
                      <Option value={25}>25 / page</Option>
                      <Option value={50}>50 / page</Option>
                      <Option value={100}>100 / page</Option>
                      <Option value={200}>200 / page</Option>
                    </Select>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Button
                      size="small"
                      icon={<LeftOutlined />}
                      disabled={pagination.current === 1}
                      onClick={() => handlePaginationChange(pagination.current - 1, pagination.pageSize)}
                    />
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (pagination.current <= 3) {
                          pageNum = i + 1;
                        } else if (pagination.current >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = pagination.current - 2 + i;
                        }

                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePaginationChange(pageNum, pagination.pageSize)}
                            style={{
                              width: 32,
                              height: 32,
                              border: pagination.current === pageNum ? "none" : "1px solid #e2e8f0",
                              borderRadius: 6,
                              background: pagination.current === pageNum
                                ? "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)"
                                : "#fff",
                              color: pagination.current === pageNum ? "#fff" : "#64748b",
                              fontWeight: 500,
                              fontSize: 13,
                              cursor: "pointer",
                              transition: "all 0.15s",
                            }}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    <Button
                      size="small"
                      icon={<RightOutlined />}
                      disabled={pagination.current >= totalPages}
                      onClick={() => handlePaginationChange(pagination.current + 1, pagination.pageSize)}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VisitorList;
